import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ImageLightboxProps {
  images: Array<{ url: string; alt?: string }>;
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

export function ImageLightbox({ images, initialIndex, isOpen, onClose }: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset index when modal opens and focus the container
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      // Focus the container for keyboard navigation
      containerRef.current?.focus();
    }
  }, [isOpen, initialIndex]);

  // Navigation handlers with useCallback to avoid duplication
  const handlePrevious = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  // Handle keyboard navigation on the container element
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
      }
    },
    [onClose, handlePrevious, handleNext]
  );

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !images[currentIndex]) {
    return null;
  }

  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      data-ui-id='image-lightbox'
      className='fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm focus:outline-none'
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      {/* Close button */}
      <button
        data-ui-id='lightbox-close-button'
        onClick={onClose}
        className='absolute top-4 right-4 z-10 rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70 focus:ring-2 focus:ring-white/50 focus:outline-none'
        aria-label='Close'
      >
        <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M6 18L18 6M6 6l12 12'
          />
        </svg>
      </button>

      {/* Navigation buttons - Previous */}
      {hasMultipleImages && (
        <button
          data-ui-id='lightbox-previous-button'
          onClick={handlePrevious}
          className='absolute top-1/2 left-4 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70 focus:ring-2 focus:ring-white/50 focus:outline-none'
          aria-label='Previous image'
        >
          <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M15 19l-7-7 7-7'
            />
          </svg>
        </button>
      )}

      {/* Navigation buttons - Next */}
      {hasMultipleImages && (
        <button
          data-ui-id='lightbox-next-button'
          onClick={handleNext}
          className='absolute top-1/2 right-4 z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70 focus:ring-2 focus:ring-white/50 focus:outline-none'
          aria-label='Next image'
        >
          <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 5l7 7-7 7' />
          </svg>
        </button>
      )}

      {/* Image container */}
      <div className='relative flex h-full w-full items-center justify-center p-4 md:p-12'>
        <img
          src={currentImage.url}
          alt={currentImage.alt ?? `Image ${currentIndex + 1}`}
          className='max-h-full max-w-full object-contain'
          onClick={e => e.stopPropagation()}
        />
      </div>

      {/* Image counter and alt text */}
      <div className='absolute bottom-4 left-1/2 z-10 -translate-x-1/2 space-y-2'>
        {/* Counter */}
        {hasMultipleImages && (
          <div className='rounded-full bg-black/50 px-4 py-2 text-center text-sm text-white'>
            {currentIndex + 1} / {images.length}
          </div>
        )}

        {/* Alt text */}
        {currentImage.alt && (
          <div className='max-w-xl rounded-lg bg-black/50 px-4 py-2 text-center text-sm text-white'>
            {currentImage.alt}
          </div>
        )}
      </div>
    </div>
  );
}
